import {isFilled} from '@prismicio/client'
import {PrismicNextLink} from '@prismicio/next/pages'
import cn from 'classnames'
import {useLinkResolver} from 'prismicio'
import type {LinksColumnSliceDefault} from 'prismicio-types'
import {track} from 'src/common/helpers'

const LinksDefaultColumn = ({
  slice,
  context: {shouldHideTitle = false, pageUid},
  componentIndex,
}: {
  slice: LinksColumnSliceDefault
  context: {shouldHideTitle?: boolean; pageUid: string}
  componentIndex: number
}): JSX.Element => {
  const {linkResolver} = useLinkResolver()

  const regularLinks = slice.primary.links.filter((link) => !link.is_feature)
  const featureLinks = slice.primary.links.filter((link) => link.is_feature)

  return (
    <div
      className="col-span-full flex w-full flex-col pt-4 text-moss-black lg:col-span-4 lg:gap-2 lg:p-0"
      data-slice-variation={slice.variation}
    >
      {isFilled.keyText(slice.primary.column_title) && (
        <span
          className={cn('text-body-bold lg:text-body-bold-l w-full pb-3 lg:px-3 lg:pb-0', {
            'max-lg:hidden': shouldHideTitle,
          })}
        >
          {slice.primary.column_title}
        </span>
      )}
      <ul className="grid w-full list-none grid-cols-2 gap-x-4 gap-y-3 lg:grid-cols-1 lg:gap-y-0">
        {regularLinks.map((link, index) => (
          <li key={link.link_label! + index} className="grow basis-0">
            <PrismicNextLink
              href={linkResolver(link.link_url)}
              onClick={() => {
                track({
                  event: 'website_click',
                  dataType: `order-${componentIndex}`,
                  location: 'main-nav-drop-down',
                  mktgId: `category-${slice.primary.marketing_id}-${index}`,
                  pageName: pageUid,
                })
              }}
            >
              <div className="flex flex-col gap-1 rounded-lg hover:bg-moss-white hover:outline hover:outline-1 hover:outline-grey-120 lg:p-3">
                <span className="text-body-bold-xs lg:text-body-bold">{link.link_label}</span>
                <span className="text-body-xs lg:text-body-s">{link.link_description}</span>
              </div>
            </PrismicNextLink>
          </li>
        ))}
      </ul>
      {featureLinks.map((link, index) => (
        <PrismicNextLink
          key={`feature-${link.link_label}-${index}`}
          href={linkResolver(link.link_url)}
          onClick={() => {
            track({
              event: 'website_click',
              dataType: `order-${componentIndex}`,
              location: 'main-nav-drop-down-feature',
              mktgId: `category-${slice.primary.marketing_id}-feature-${index}`,
              pageName: pageUid,
            })
          }}
          className="mt-3 block rounded-[4px] bg-green-dark p-5 transition-opacity hover:opacity-90 lg:mx-3"
        >
          {isFilled.keyText(link.feature_eyebrow) && (
            <span className="text-[11px] font-semibold uppercase tracking-widest text-green-neon">
              {link.feature_eyebrow}
            </span>
          )}
          <div className="mt-1.5 flex flex-col gap-1">
            <span className="text-[18px] font-normal text-moss-white">{link.link_label}</span>
            {isFilled.keyText(link.link_description) && (
              <span className="text-[13px] leading-snug text-moss-white/60">{link.link_description}</span>
            )}
          </div>
        </PrismicNextLink>
      ))}
    </div>
  )
}

export default LinksDefaultColumn
